import React from 'react';
import { X, FileText, Clock, GitBranch, Play, Eye } from 'lucide-react';
import './DraftPreviewModal.css';

interface DraftPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestore: () => void;
  draft: {
    id: string;
    name: string;
    description?: string;
    current_phase: number;
    files: Array<{ filename: string; file_type: string }>;
    last_saved: string;
    generated_scripts?: Array<{ code: string }>;
    requirements_analysis?: string;
  };
}

export const DraftPreviewModal: React.FC<DraftPreviewModalProps> = ({
  isOpen,
  onClose,
  onRestore,
  draft
}) => {
  if (!isOpen) return null;

  const getPhaseLabel = (phase: number) => {
    switch (phase) {
      case 1:
        return 'Requirements Analysis';
      case 2:
        return 'Script Generation';
      case 3:
        return 'Quality Review';
      case 4:
        return 'Revision';
      default:
        return `Phase ${phase}`;
    }
  };

  const getPhaseProgress = (phase: number) => {
    return (phase / 4) * 100;
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const hasGeneratedScript = draft.generated_scripts && draft.generated_scripts.length > 0;
  const latestScript = hasGeneratedScript && draft.generated_scripts ? draft.generated_scripts[draft.generated_scripts.length - 1] : null;

  return (
    <div className="modal-overlay">
      <div className="modal-container draft-preview-modal">
        <div className="modal-header">
          <h2>Resume Draft Workflow</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="draft-info">
            <h3>{draft.name}</h3>
            {draft.description && (
              <p className="draft-description">{draft.description}</p>
            )}
            <p className="draft-timestamp">
              <Clock size={16} />
              Last saved {formatTimestamp(draft.last_saved)}
            </p>
          </div>

          <div className="draft-progress">
            <div className="progress-header">
              <span className="progress-label">Progress</span>
              <span className="progress-phase">{getPhaseLabel(draft.current_phase)}</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${getPhaseProgress(draft.current_phase)}%` }}
              />
            </div>
            <div className="phase-indicators">
              {[1, 2, 3, 4].map(phase => (
                <div 
                  key={phase}
                  className={`phase-indicator ${phase <= draft.current_phase ? 'completed' : ''}`}
                  title={getPhaseLabel(phase)}
                >
                  {phase}
                </div>
              ))}
            </div>
          </div>

          <div className="draft-details">
            {draft.files && draft.files.length > 0 && (
              <div className="detail-section">
                <h4>
                  <FileText size={16} />
                  Files ({draft.files.length})
                </h4>
                <div className="file-list">
                  {draft.files.map((file, index) => (
                    <div key={index} className="file-chip">
                      <span className="file-icon">📄</span>
                      <span className="file-name">{file.filename}</span>
                      <span className="file-type">{file.file_type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasGeneratedScript && latestScript && (
              <div className="detail-section">
                <h4>
                  <GitBranch size={16} />
                  Generated Script
                </h4>
                <div className="script-preview">
                  <pre>
                    <code>
                      {latestScript.code.split('\n').slice(0, 10).join('\n')}
                      {latestScript.code.split('\n').length > 10 && '\n...'}
                    </code>
                  </pre>
                </div>
              </div>
            )}

            {draft.requirements_analysis && (
              <div className="detail-section">
                <h4>
                  <Eye size={16} />
                  Requirements Analysis
                </h4>
                <div className="requirements-preview">
                  {draft.requirements_analysis.substring(0, 200)}
                  {draft.requirements_analysis.length > 200 && '...'}
                </div>
              </div>
            )}
          </div>

          <div className="info-message">
            <p>
              This will create a new session and restore your workflow to where you left off.
              All your files, scripts, and progress will be restored.
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onRestore}>
            <Play size={16} />
            Continue Working
          </button>
        </div>
      </div>
    </div>
  );
};